/**
 * Create Class Page
 * Form to create a new class
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { apiRequest } from '@/api/client';
import { sanitizeClassData } from '@/utils/inputSanitizer';
import { getUserFriendlyError } from '@/utils/errorMessages';

interface ClassFormData {
  className: string;
  gradeLevel: string;
  ageRating: string;
}

const ageOptions = [
  { 
    label: 'Toddlers (2-4)', 
    value: 'toddlers',
    colourBands: ['pink', 'red']
  },
  { 
    label: 'Children (5-8)', 
    value: 'children',
    colourBands: ['yellow', 'blue', 'green', 'orange', 'turquoise', 'purple']
  },
  { 
    label: 'Young Teens (9-12)', 
    value: 'young_teens',
    colourBands: ['gold', 'white', 'lime']
  },
  { 
    label: 'Teens (13-17)', 
    value: 'teens',
    colourBands: []
  }
];

const colourBands: Record<string, { color: string; level: number; ageRange: string; description: string }> = {
  pink: { color: '#FFC0CB', level: 1, ageRange: '4-5', description: 'Simplest - repetitive text, 20-50 words' },
  red: { color: '#FF6B6B', level: 2, ageRange: '4-5', description: 'Building confidence - 50-100 words' },
  yellow: { color: '#FFD93D', level: 3, ageRange: '5-6', description: 'Varied patterns - 100-150 words' },
  blue: { color: '#6BCF7F', level: 4, ageRange: '5-6', description: 'Longer stories - 150-250 words' },
  green: { color: '#4CAF50', level: 5, ageRange: '6-7', description: 'Building stamina - 250-400 words' },
  orange: { color: '#FF9800', level: 6, ageRange: '6-7', description: 'Independent reading - 400-600 words' },
  turquoise: { color: '#40E0D0', level: 7, ageRange: '7-8', description: 'Confident readers - 600-800 words' },
  purple: { color: '#9C27B0', level: 8, ageRange: '7-8', description: 'Fluent readers - 800-1000 words' },
  gold: { color: '#FFD700', level: 9, ageRange: '8-9', description: 'Advanced readers - 1000-1500 words' },
  white: { color: '#E0E0E0', level: 10, ageRange: '9-10', description: 'Highly competent - 1500-2000 words' },
  lime: { color: '#CDDC39', level: 11, ageRange: '10+', description: 'Expert readers - 2000-3000 words' }
};

export const CreateClassPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState<ClassFormData>({
    className: '',
    gradeLevel: '',
    ageRating: 'children',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ClassFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const handleChange = (field: keyof ClassFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const toggleCategory = (categoryValue: string) => {
    setExpandedCategory(expandedCategory === categoryValue ? null : categoryValue);
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ClassFormData, string>> = {};

    if (!formData.className.trim()) {
      newErrors.className = 'Class name is required';
    }

    if (!formData.gradeLevel.trim()) {
      newErrors.gradeLevel = 'Year level is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Sanitize form data before submission
      const sanitized = sanitizeClassData({
        className: formData.className,
        yearGroup: formData.gradeLevel,
        ageRating: formData.ageRating === 'children' ? 10 : 13,
        teacherId: user?.email || ''
      });

      const response = await apiRequest('create_class', {
        userEmail: user?.email,
        className: sanitized.className,
        gradeLevel: sanitized.yearGroup,
        ageRating: formData.ageRating,
      });

      if (response.success) {
        showNotification('success', 'Class created successfully!');
        navigate('/classes');
      } else {
        showNotification('error', getUserFriendlyError(response, 'Class'));
      }
    } catch (error) {
      showNotification('error', getUserFriendlyError(error, 'Class'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Create New Class</h1>
        <p className="text-slate-600 mt-2">Set up a new class for your students</p>
      </div>

      {/* Form */}
      <Card>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Class Name"
            type="text"
            value={formData.className}
            onChange={(e) => handleChange('className', e.target.value)}
            error={errors.className}
            placeholder="e.g., Year 5 English"
            required
          />

          <Input
            label="Year Level"
            type="text"
            value={formData.gradeLevel}
            onChange={(e) => handleChange('gradeLevel', e.target.value)}
            error={errors.gradeLevel}
            placeholder="e.g., 5"
            required
          />

          {/* Age Rating / Colour Bands */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Reading Level *
            </label>
            <p className="text-sm text-slate-500 mb-3">
              Select an age group or expand to see Oxford Reading Tree colour bands
            </p>
            
            {ageOptions.map((rating) => (
              <div key={rating.value} className="mb-3">
                {/* Main age rating row */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleChange('ageRating', rating.value)}
                    className={`flex-1 px-4 py-3 border rounded-xl text-left transition-colors ${
                      formData.ageRating === rating.value
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                        : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                    }`}
                  >
                    <div className="font-medium">{rating.label}</div>
                  </button>
                  
                  {/* Expand button for colour bands */}
                  {rating.colourBands && rating.colourBands.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleCategory(rating.value)}
                      className="px-4 py-3 border border-slate-300 rounded-xl bg-white hover:bg-slate-50 transition-colors"
                    >
                      <div className="text-center">
                        <div className="text-lg">{expandedCategory === rating.value ? '▼' : '▶'}</div>
                        <div className="text-xs text-slate-600">Bands</div>
                      </div>
                    </button>
                  )}
                </div>

                {/* Expanded colour bands */}
                {expandedCategory === rating.value && rating.colourBands && rating.colourBands.length > 0 && (
                  <div className="mt-3 ml-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <p className="text-sm font-medium text-slate-700 mb-3">
                      Oxford Reading Tree Colour Bands:
                    </p>
                    <div className="space-y-2">
                      {rating.colourBands.map((bandName) => {
                        const band = colourBands[bandName];
                        return (
                          <button
                            key={bandName}
                            type="button"
                            onClick={() => handleChange('ageRating', bandName)}
                            className={`w-full flex items-center gap-3 px-4 py-3 border rounded-lg text-left transition-colors ${
                              formData.ageRating === bandName
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                            }`}
                          >
                            <div
                              className="w-6 h-6 rounded-full border-2 border-slate-300"
                              style={{ backgroundColor: band.color }}
                            />
                            <div className="flex-1">
                              <div className="font-medium text-slate-900">
                                {bandName.charAt(0).toUpperCase() + bandName.slice(1)} Band
                              </div>
                              <div className="text-sm text-slate-600">
                                Level {band.level} • Ages {band.ageRange} • {band.description}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {errors.ageRating && (
              <p className="text-red-600 text-sm mt-1">{errors.ageRating}</p>
            )}
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/classes')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              className="flex-1"
            >
              Create Class
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
